import { useState, useEffect } from 'react';
import { Building, Save, RotateCcw, CreditCard, Globe, FileText, Eye } from 'lucide-react';

const BankingSettings = ({ settings, onSave, onChange }) => {
  const [formData, setFormData] = useState({
    bankName: settings?.bankName || '',
    bankAddress: settings?.bankAddress || '',
    sortCode: settings?.sortCode || '',
    accountNumber: settings?.accountNumber || '',
    iban: settings?.iban || '',
    bic: settings?.bic || '',
    defaultPaymentTerms: settings?.defaultPaymentTerms || 'Net 30',
    paymentMethods: settings?.paymentMethods || 'Bank Transfer, Cheque',
    paymentInstructions: settings?.paymentInstructions || '',
    currency: settings?.currency || '£'
  });

  const [activeTab, setActiveTab] = useState('currency');
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    if (onChange) {
      onChange();
    }
  }, [formData, onChange]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({ ...settings, ...formData });
  };

  const paymentTermsOptions = [
    'Due on Receipt',
    'Net 7',
    'Net 15',
    'Net 30',
    'Net 45',
    'Net 60',
    'Net 90'
  ];

  const currencyOptions = [
    { value: '£', label: '£ (GBP) - British Pound' },
    { value: '$', label: '$ (USD) - US Dollar' },
    { value: '€', label: '€ (EUR) - Euro' },
    { value: '¥', label: '¥ (JPY) - Japanese Yen' },
    { value: 'C$', label: 'C$ (CAD) - Canadian Dollar' },
    { value: 'A$', label: 'A$ (AUD) - Australian Dollar' },
    { value: 'CHF', label: 'CHF - Swiss Franc' },
    { value: 'kr', label: 'kr (SEK/NOK/DKK) - Scandinavian' },
    { value: '₹', label: '₹ (INR) - Indian Rupee' }
  ];

  const formatSortCode = (value) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length >= 6) {
      return `${digits.slice(0, 2)}-${digits.slice(2, 4)}-${digits.slice(4, 6)}`;
    } else if (digits.length >= 4) {
      return `${digits.slice(0, 2)}-${digits.slice(2, 4)}-${digits.slice(4)}`;
    } else if (digits.length >= 2) {
      return `${digits.slice(0, 2)}-${digits.slice(2)}`;
    }
    return digits;
  };

  const handleSortCodeChange = (e) => {
    const formatted = formatSortCode(e.target.value);
    setFormData({ ...formData, sortCode: formatted });
  };

  const tabs = [
    { id: 'currency', label: 'Currency', icon: Globe },
    { id: 'banking', label: 'Bank Details', icon: Building },
    { id: 'payment', label: 'Payment Terms', icon: CreditCard },
    { id: 'instructions', label: 'Instructions', icon: FileText }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'currency':
        return (
          <div className="space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-blue-900 mb-2">Currency Settings</h3>
              <p className="text-blue-700 text-sm mb-4">Set your default currency for all invoices and quotes</p>
              
              <div className="max-w-md">
                <label className="block text-sm font-medium text-gray-700 mb-2">Default Currency</label>
                <select
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-lg"
                  value={formData.currency}
                  onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                >
                  {currencyOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                  <p className="text-sm text-yellow-800">
                    💡 This currency will be automatically selected for new invoices. You can still change it per invoice if needed.
                  </p>
                </div>
              </div>
            </div>
          </div>
        );

      case 'banking':
        return (
          <div className="space-y-6">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-green-900 mb-2">Bank Account Details</h3>
              <p className="text-green-700 text-sm mb-6">Add your bank details to include on invoices for customer payments</p>
              
              <div className="space-y-6">
                {/* Bank Info */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Bank Name *</label>
                    <input
                      type="text"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                      value={formData.bankName}
                      onChange={(e) => setFormData({ ...formData, bankName: e.target.value })}
                      placeholder="e.g., Barclays Bank"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Bank Address</label>
                    <input
                      type="text"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                      value={formData.bankAddress}
                      onChange={(e) => setFormData({ ...formData, bankAddress: e.target.value })}
                      placeholder="Bank branch address"
                    />
                  </div>
                </div>

                {/* UK Banking Details */}
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-800 mb-3">UK Banking Details</h4>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Sort Code *</label>
                      <input
                        type="text"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none font-mono"
                        value={formData.sortCode}
                        onChange={handleSortCodeChange}
                        placeholder="12-34-56"
                        maxLength="8"
                      />
                      <p className="text-xs text-gray-500 mt-1">6-digit sort code (automatically formatted)</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Account Number *</label>
                      <input
                        type="text"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none font-mono"
                        value={formData.accountNumber}
                        onChange={(e) => setFormData({ ...formData, accountNumber: e.target.value.replace(/\D/g, '') })}
                        placeholder="12345678"
                        maxLength="8"
                      />
                      <p className="text-xs text-gray-500 mt-1">8-digit account number</p>
                    </div>
                  </div>
                </div>

                {/* International Details */}
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-800 mb-3">International Transfers (Optional)</h4>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">IBAN</label>
                      <input
                        type="text"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none font-mono text-sm"
                        value={formData.iban}
                        onChange={(e) => setFormData({ ...formData, iban: e.target.value.toUpperCase() })}
                        placeholder="GB29 NWBK 6016 1331 9268 19"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">BIC/SWIFT Code</label>
                      <input
                        type="text"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none font-mono"
                        value={formData.bic}
                        onChange={(e) => setFormData({ ...formData, bic: e.target.value.toUpperCase() })}
                        placeholder="BARCGB22"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'payment':
        return (
          <div className="space-y-6">
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-purple-900 mb-2">Payment Terms & Methods</h3>
              <p className="text-purple-700 text-sm mb-6">Configure how customers can pay you and payment deadlines</p>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Default Payment Terms</label>
                  <select
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    value={formData.defaultPaymentTerms}
                    onChange={(e) => setFormData({ ...formData, defaultPaymentTerms: e.target.value })}
                  >
                    {paymentTermsOptions.map(term => (
                      <option key={term} value={term}>{term}</option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">How many days customers have to pay</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Accepted Payment Methods</label>
                  <input
                    type="text"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    value={formData.paymentMethods}
                    onChange={(e) => setFormData({ ...formData, paymentMethods: e.target.value })}
                    placeholder="Bank Transfer, Cheque, Card"
                  />
                  <p className="text-xs text-gray-500 mt-1">Separate with commas</p>
                </div>
              </div>
            </div>
          </div>
        );

      case 'instructions':
        return (
          <div className="space-y-6">
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-orange-900 mb-2">Payment Instructions</h3>
              <p className="text-orange-700 text-sm mb-6">Add specific instructions that will appear on every invoice</p>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Custom Payment Instructions</label>
                <textarea
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none resize-none"
                  rows="6"
                  value={formData.paymentInstructions}
                  onChange={(e) => setFormData({ ...formData, paymentInstructions: e.target.value })}
                  placeholder="Please reference invoice number when making payment. All payments should be made in GBP. Late payments may incur additional charges..."
                />
                <div className="flex justify-between items-center mt-2">
                  <p className="text-xs text-gray-500">These instructions will appear on all invoices</p>
                  <p className="text-xs text-gray-400">{formData.paymentInstructions.length}/500 characters</p>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex justify-between items-start">
          <div>
            <h5 className="flex items-center text-xl font-semibold text-gray-800">
              <Building size={24} className="mr-3" />
              Banking & Payment Settings
            </h5>
            <p className="text-sm text-gray-600 mt-1">
              Configure your payment details and banking information
            </p>
          </div>
          <button
            onClick={() => setShowPreview(!showPreview)}
            className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <Eye size={16} className="mr-2" />
            {showPreview ? 'Hide Preview' : 'Show Preview'}
          </button>
        </div>
      </div>

      <div className="p-6">
        <div>
          {/* Tab Navigation */}
          <div className="border-b border-gray-200 mb-6">
            <nav className="flex space-x-8">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center px-1 py-4 text-sm font-medium border-b-2 transition-colors ${
                      activeTab === tab.id
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Icon size={16} className="mr-2" />
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Tab Content */}
          {renderTabContent()}

          {/* Preview Section */}
          {showPreview && (
            <div className="mt-8 bg-gray-50 border border-gray-200 rounded-lg p-6">
              <h6 className="text-lg font-semibold text-gray-800 mb-4">Invoice Preview</h6>
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-3">Bank Details</h4>
                    {formData.bankName ? (
                      <div className="text-sm text-gray-700 space-y-1">
                        <div className="font-medium">{formData.bankName}</div>
                        {formData.bankAddress && <div>{formData.bankAddress}</div>}
                        {formData.sortCode && <div>Sort Code: {formData.sortCode}</div>}
                        {formData.accountNumber && <div>Account: {formData.accountNumber}</div>}
                        {formData.iban && <div>IBAN: {formData.iban}</div>}
                        {formData.bic && <div>BIC: {formData.bic}</div>}
                      </div>
                    ) : (
                      <em className="text-gray-500 text-sm">Bank details will appear here</em>
                    )}
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-3">Payment Information</h4>
                    <div className="text-sm text-gray-700 space-y-2">
                      <div><span className="font-medium">Terms:</span> {formData.defaultPaymentTerms}</div>
                      <div><span className="font-medium">Methods:</span> {formData.paymentMethods}</div>
                      <div><span className="font-medium">Currency:</span> {formData.currency}</div>
                      {formData.paymentInstructions && (
                        <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded">
                          <div className="font-medium text-blue-800 mb-1">Payment Instructions:</div>
                          <div className="text-blue-700">{formData.paymentInstructions}</div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-between pt-8 border-t border-gray-200 mt-8">
            <button 
              type="button" 
              className="flex items-center px-6 py-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <RotateCcw size={16} className="mr-2" />
              Reset to Defaults
            </button>
            <button 
              type="button"
              onClick={handleSubmit}
              className="flex items-center px-6 py-3 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Save size={16} className="mr-2" />
              Save All Settings
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BankingSettings;